import {
  stripHtml,
  stripHyphens,
  stripUnderscores,
  capitalizeWords
} from 'oxygen-lib-ui/helpers/strings';
import { imageUrl } from 'util/images';
import { convertTimeToComponents } from './helpers';

export function getRecipeIngredients(groups) {
  if (!groups || !groups.length) {
    return {};
  }
  return {
    recipeIngredient: [].concat(
      ...groups.map(group =>
        group.items.map(({ html = '', quantity = '', unit = '' }) =>
          // simply joining ' ' can result in an extra empty space when quantity or unit is ''
          // so we strip all '' before we join everything together
          [quantity, unit, stripHtml(html)]
            .filter(str => [str].join(' ').length > 0)
            .join(' ')
        )
      )
    )
  };
}

export function getRecipeAuthor(author) {
  return !author
    ? {}
    : {
        author: {
          '@type': 'Person',
          name: author.name
        }
      };
}

export function getRecipeAggregateRating(ratingsData) {
  return ratingsData && ratingsData.totalRatings > 0
    ? {
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: ratingsData.average,
          ratingCount: ratingsData.totalRatings
        }
      }
    : {};
}

export function getRecipeTime(key, time) {
  const parsedTime = convertTimeToComponents(time);
  return parsedTime
    ? {
        [key]: parsedTime.isoMin
      }
    : {};
}

function formatSingleInstructionGroup(group) {
  if (!group.items || !group.items.length) {
    return [];
  }

  return group.items.map(step => ({
    '@type': 'HowToStep',
    text: stripHtml(step.html)
  }));
}

export function getRecipeInstructions(groups) {
  if (!groups || !groups.length) {
    return {};
  }

  if (groups.length === 1) {
    return {
      recipeInstructions: formatSingleInstructionGroup(...groups)
    };
  }

  return {
    recipeInstructions: groups.map(group => {
      if (!group.items || !group.items.length) {
        return [];
      }

      return {
        '@type': 'HowToSection',
        name: group.name,
        itemListElement: group.items.map(step => ({
          '@type': 'HowToStep',
          text: stripHtml(step.html)
        }))
      };
    })
  };
}

export function getRecipeYield(makes, serves) {
  if (serves) {
    return {
      recipeYield: `Serves ${serves}`
    };
  }

  if (!serves && makes) {
    return {
      recipeYield: `Makes ${makes}`
    };
  }

  return {};
}

export function getRecipeNutrition(nutritionalGuide) {
  if (!nutritionalGuide) {
    return {};
  }

  const { calories, servings, primaryNutrients = [] } = nutritionalGuide;
  const nutrients = primaryNutrients.reduce((acc, current) => {
    const key = current.schemaValue !== 'false' ? current.schemaValue : '';
    if (key) {
      acc[key] = current.quantity;
    }

    return acc;
  }, {});

  return {
    nutrition: Object.assign(
      {},
      {
        '@type': 'NutritionInformation',
        calories: `${Math.round(calories / servings)}`,
        servingSize: servings
      },
      nutrients
    )
  };
}

const getNames = levels =>
  levels.reduce((a, level) => {
    let names = level.name ? [level.name] : [];
    if (level.nextLevel) {
      names = names.concat(getNames(level.nextLevel));
    }
    return a.concat(names);
  }, []);

export function getRecipeKeywords({
  keywords,
  timing,
  mainIngredients,
  occasion,
  dishType,
  lifestyle
}) {
  let keywordsFull = keywords || [];

  if (timing && timing.length) {
    keywordsFull = keywordsFull.concat(getNames(timing));
  }

  if (mainIngredients && mainIngredients.length) {
    keywordsFull = keywordsFull.concat(getNames(mainIngredients));
  }

  if (occasion && occasion.length) {
    keywordsFull = keywordsFull.concat(getNames(occasion));
  }

  if (dishType && dishType.length) {
    keywordsFull = keywordsFull.concat(getNames(dishType));
  }

  if (lifestyle && lifestyle.length) {
    keywordsFull = keywordsFull.concat(getNames(lifestyle));
  }
  if (keywordsFull.length) {
    keywordsFull = [
      ...new Set(
        keywordsFull
          .map(stripHyphens)
          .map(stripUnderscores)
          .map(c => c.trim())
          .map(c => c.toLowerCase())
      )
    ].sort();
    return {
      keywords: keywordsFull.join(', ')
    };
  }

  return {};
}

export function getRecipeCategories(categories, meal, course, dishType, diet) {
  let enrichedCategories = [];
  if (categories && categories.length) {
    enrichedCategories = enrichedCategories.concat(categories);
  }
  if (meal && meal.length) {
    enrichedCategories = enrichedCategories.concat(meal);
  }
  if (course && course.length) {
    enrichedCategories = enrichedCategories.concat(course);
  }
  if (dishType && dishType.length) {
    enrichedCategories = enrichedCategories.concat(dishType);
  }
  if (diet && diet.length) {
    enrichedCategories = enrichedCategories.concat(diet);
  }

  if (enrichedCategories.length) {
    return {
      recipeCategory: enrichedCategories
        // Filter out falsy values, e.g. null or ''
        .filter(c => !!c.name)
        .map(c => c.name)
        .map(capitalizeWords)
    };
  }

  return {};
}

export function getRecipeCuisine(cuisine) {
  if (cuisine && cuisine.length) {
    return {
      recipeCuisine: [...new Set(getNames(cuisine).map(capitalizeWords))]
    };
  }

  return {};
}

export function getRecipeDescription(description) {
  return description ? { description: stripHtml(description) } : {};
}

export function getRecipeCookingMethod(method) {
  if (method && method.length) {
    return {
      cookingMethod: [...new Set(getNames(method).map(capitalizeWords))]
    };
  }

  return {};
}

export function buildRecipeStructuredData({
  title,
  description,
  imageUrl: thumbUrl,
  ingredientGroups,
  keywords,
  categories,
  author,
  ratingsData,
  cookTime,
  prepTime,
  instructionGroups,
  makes,
  serves,
  nutritionalGuide,
  course,
  meal,
  dishType,
  diet,
  cuisine,
  method,
  timing,
  mainIngredients,
  occasion,
  lifestyle
}) {
  const imageOps = {
    width: 1500,
    format: 'jpg',
    resizeMode: 'crop'
  };
  return Object.assign(
    {
      '@context': 'https://schema.org',
      '@type': 'Recipe',
      name: title,
      image: imageUrl
        ? [
            imageUrl(thumbUrl, { ...imageOps, aspectRatio: '16:9' }),
            imageUrl(thumbUrl, { ...imageOps, aspectRatio: '4:3' }),
            imageUrl(thumbUrl, { ...imageOps, aspectRatio: '1:1' })
          ]
        : ''
    },
    getRecipeDescription(description),
    getRecipeCategories(categories, meal, course, dishType, diet),
    getRecipeCuisine(cuisine),
    getRecipeKeywords({
      keywords,
      timing,
      mainIngredients,
      occasion,
      dishType,
      lifestyle
    }),
    getRecipeIngredients(ingredientGroups),
    getRecipeAuthor(author),
    getRecipeAggregateRating(ratingsData),
    getRecipeTime('cookTime', cookTime),
    getRecipeTime('prepTime', prepTime),
    getRecipeInstructions(instructionGroups),
    getRecipeYield(makes, serves),
    getRecipeNutrition(nutritionalGuide),
    getRecipeCookingMethod(method)
  );
}

export default buildRecipeStructuredData;
